/**
 * VÉRAULT Portal — Inbox drawer.
 *
 * The slide-in panel that surfaces queued confirmation-email imports.
 * Opens from the Bookings page "N waiting" pill. Each row: parsed preview,
 * a quick attach-to-trip flow, and an archive action.
 */
import React from "react";
import { X, Sparkles, Trash2, ArrowRight, Mail } from "lucide-react";
import { Surface, PortalButton, Empty, Skeleton } from "../index";
import { api } from "../lib/api";
import * as fmt from "../lib/format";

function AttachInline({ row, trips, clients, onAttached }) {
  const [tripId, setTripId] = React.useState(row.attached_trip_id || "");
  const [clientId, setClientId] = React.useState(row.attached_client_id || "");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Auto-pick client when trip is chosen
  React.useEffect(() => {
    if (!tripId) return;
    const t = trips.find((x) => x.id === tripId);
    if (t?.client_id && !clientId) setClientId(t.client_id);
  }, [tripId, trips, clientId]);

  const attach = async () => {
    if (!tripId && !clientId) {
      setError("Pick a trip or a client.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/inbox/imports/${row.id}/attach`, {
        trip_id: tripId || null,
        client_id: clientId || null,
      });
      onAttached();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't attach.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-[rgba(0,0,0,0.05)] space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <select
          value={tripId}
          onChange={(e) => setTripId(e.target.value)}
          data-testid={`inbox-trip-${row.id}`}
          className="rounded-md border border-[rgba(0,0,0,0.08)] bg-white/70 px-3 py-2 font-body text-[12.5px] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent-gold))]"
        >
          <option value="">— Trip —</option>
          {trips.map((t) => (
            <option key={t.id} value={t.id}>
              {t.internal_trip_name || t.client_trip_title || t.destinations || t.id.slice(0, 8)}
            </option>
          ))}
        </select>
        <select
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          data-testid={`inbox-client-${row.id}`}
          className="rounded-md border border-[rgba(0,0,0,0.08)] bg-white/70 px-3 py-2 font-body text-[12.5px] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent-gold))]"
        >
          <option value="">— Client —</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.full_name || c.email || c.id.slice(0, 8)}</option>
          ))}
        </select>
      </div>
      {error && <p className="font-body text-[12px] text-[hsl(var(--accent-terracotta))]">{error}</p>}
      <div className="flex justify-end">
        <PortalButton
          variant="gold"
          size="sm"
          onClick={attach}
          disabled={busy}
          data-testid={`inbox-attach-${row.id}`}
          iconAfter={<ArrowRight className="size-3.5" strokeWidth={1.5} />}
        >
          {busy ? "Attaching…" : "Attach"}
        </PortalButton>
      </div>
    </div>
  );
}

export function InboxDrawer({ open, onClose, onChanged }) {
  const [rows, setRows] = React.useState(null);
  const [trips, setTrips] = React.useState([]);
  const [clients, setClients] = React.useState([]);

  const refresh = React.useCallback(() => {
    setRows(null);
    api.get("/inbox/imports", { params: { status: "pending" } }).then(({ data }) => setRows(data || []));
  }, []);

  React.useEffect(() => {
    if (!open) return;
    refresh();
    if (trips.length === 0) {
      Promise.all([
        api.get("/travels/trips").then((r) => r.data).catch(() => []),
        api.get("/travels/clients").then((r) => r.data).catch(() => []),
      ]).then(([t, c]) => {
        setTrips(Array.isArray(t) ? t : []);
        setClients(Array.isArray(c) ? c : []);
      });
    }
  }, [open, refresh, trips.length]);

  const archive = async (id) => {
    await api.post(`/inbox/imports/${id}/archive`);
    refresh();
    onChanged?.();
  };

  const onAttached = () => {
    refresh();
    onChanged?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9990] flex justify-end" data-testid="inbox-drawer">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[6px]" onClick={onClose} />
      <aside className="relative w-full max-w-md bg-[hsl(var(--background))] border-l border-[rgba(0,0,0,0.05)] shadow-[-30px_0_80px_-20px_rgba(0,0,0,0.25)] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-[hsl(var(--background))]/95 backdrop-blur px-6 py-4 border-b border-[rgba(0,0,0,0.06)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Inbox</p>
              <p className="font-serif text-lg">Confirmation imports</p>
            </div>
          </div>
          <button onClick={onClose} className="size-9 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--background-paper))]" aria-label="Close">
            <X className="size-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {!rows ? (
            <div className="space-y-2">
              {[0, 1].map((i) => <Skeleton key={i} className="h-24" />)}
            </div>
          ) : rows.length === 0 ? (
            <Empty
              title="Nothing waiting"
              hint="Forwarded emails (or pasted confirmations) will appear here, parsed and ready to attach."
              icon={<Mail className="size-6" strokeWidth={1.25} />}
            />
          ) : (
            rows.map((r) => {
              const p = r.parsed || {};
              return (
                <Surface variant="default" padded className="!p-5" key={r.id} data-testid={`inbox-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                        Confidence · {Math.round((p.confidence || 0) * 100)}% · {r.source}
                      </p>
                      <p className="mt-1 font-serif text-base text-[hsl(var(--foreground))] truncate">
                        {p.vendor || r.subject || "Confirmation"}
                      </p>
                      <p className="mt-0.5 font-accent italic text-[12.5px] text-[hsl(var(--foreground-secondary))] truncate">
                        {[p.booking_type, p.confirmation_number].filter(Boolean).join(" · ") || "—"}
                      </p>
                    </div>
                    <button
                      onClick={() => archive(r.id)}
                      title="Archive"
                      data-testid={`inbox-archive-${r.id}`}
                      className="size-8 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))] hover:bg-[hsl(var(--background-paper))]"
                    >
                      <Trash2 className="size-3.5" strokeWidth={1.5} />
                    </button>
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-2 text-[12px]">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">Cost</p>
                      <p className="font-body">{p.quoted_cost ? fmt.money(p.quoted_cost, p.currency) : "—"}</p>
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">Start</p>
                      <p className="font-body">{fmt.formatDate(p.start_date) || "—"}</p>
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">End</p>
                      <p className="font-body">{fmt.formatDate(p.end_date) || "—"}</p>
                    </div>
                  </div>

                  <AttachInline row={r} trips={trips} clients={clients} onAttached={onAttached} />
                </Surface>
              );
            })
          )}
        </div>
      </aside>
    </div>
  );
}

/**
 * InboxBell — the small "N waiting" pill that lives on the Bookings page header.
 * Polls the lightweight /counts endpoint every 60s.
 */
export function InboxBell({ onClick }) {
  const [pending, setPending] = React.useState(0);
  const tick = React.useCallback(() => {
    api.get("/inbox/imports/counts").then(({ data }) => setPending(data?.pending || 0)).catch(() => {});
  }, []);
  React.useEffect(() => {
    tick();
    const id = setInterval(tick, 60_000);
    const handler = () => tick();
    window.addEventListener("verault:inbox-changed", handler);
    return () => {
      clearInterval(id);
      window.removeEventListener("verault:inbox-changed", handler);
    };
  }, [tick]);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="inbox-bell"
      className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--background-paper))] hover:bg-[hsl(var(--background-stone))]/70 border border-[rgba(0,0,0,0.06)] px-3 h-10 font-body text-[13px] tracking-tight transition-colors"
      title="Confirmation inbox"
    >
      <Mail className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
      <span className="text-[hsl(var(--foreground))]">Inbox</span>
      {pending > 0 && (
        <span className="ml-0.5 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[hsl(var(--accent-gold))] text-white font-mono text-[10px] font-semibold tracking-wider">
          {pending}
        </span>
      )}
    </button>
  );
}
